import React, { useState, useEffect, useCallback } from 'react'
import { Search, ArrowUpCircle, ArrowDownCircle, ChevronLeft, ChevronRight, Info, ShieldCheck, Check, X, Filter, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react'
import { apiClient } from '@/services/api'
import { handleApiError, showSuccessToast } from '@/utils/errorHandling'
import { formatRelativeDate, getAvatarUrl } from '@/utils'
import { useIsAdmin } from '@/store/authStore'
import { Navigate } from 'react-router-dom'

interface UserRow {
  id: string
  email: string
  display_name: string | null
  avatar_url: string | null
  role?: string
  created_at?: string
}

interface OnboardingRow {
  id: string
  email: string
  display_name: string | null
  avatar_url: string | null
  role?: string
  created_at?: string
  country: string | null
  institution: string | null
  grades: string[]
  subjects: string[]
  using_potato_pirates: string | null
  onboarding_completed: boolean
}

interface Pagination {
  current_page: number
  per_page: number
  total_pages: number
  total_count: number
  has_next_page: boolean
  has_prev_page: boolean
}

type Tab = 'users' | 'onboarding'

export function UserManagement() {
  const isAdmin = useIsAdmin()
  const [activeTab, setActiveTab] = useState<Tab>('users')

  // --- Users tab state ---
  const [users, setUsers] = useState<UserRow[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [page, setPage] = useState(1)
  const [pagination, setPagination] = useState<Pagination | null>(null)
  const [actionLoading, setActionLoading] = useState<string | null>(null)

  // --- Onboarding tab state ---
  const [obUsers, setObUsers] = useState<OnboardingRow[]>([])
  const [obLoading, setObLoading] = useState(true)
  const [obSearch, setObSearch] = useState('')
  const [obDebouncedSearch, setObDebouncedSearch] = useState('')
  const [obPage, setObPage] = useState(1)
  const [obPagination, setObPagination] = useState<Pagination | null>(null)
  const [obFilter, setObFilter] = useState<'all' | 'completed' | 'incomplete'>('all')
  const [obSortBy, setObSortBy] = useState<string | null>(null)
  const [obSortDir, setObSortDir] = useState<'asc' | 'desc'>('asc')

  // Debounce search (Users tab)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search)
      setPage(1)
    }, 300)
    return () => clearTimeout(timer)
  }, [search])

  // Debounce search (Onboarding tab)
  useEffect(() => {
    const timer = setTimeout(() => {
      setObDebouncedSearch(obSearch)
      setObPage(1)
    }, 300)
    return () => clearTimeout(timer)
  }, [obSearch])

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true)
      const response = await apiClient.getCaptains({
        search: debouncedSearch || undefined,
        page,
        per_page: 20,
      })
      setUsers((response.data as any) || [])
      setPagination((response as any).meta?.pagination || null)
    } catch (err) {
      handleApiError(err as any, 'load users')
    } finally {
      setLoading(false)
    }
  }, [debouncedSearch, page])

  const loadOnboardingData = useCallback(async () => {
    try {
      setObLoading(true)
      const response = await apiClient.getOnboardingData({
        search: obDebouncedSearch || undefined,
        status: obFilter === 'all' ? undefined : obFilter,
        page: obPage,
        per_page: 20,
        sort_by: obSortBy || undefined,
        sort_dir: obSortBy ? obSortDir : undefined,
      })
      setObUsers((response.data as any) || [])
      setObPagination((response as any).meta?.pagination || null)
    } catch (err) {
      handleApiError(err as any, 'load onboarding data')
    } finally {
      setObLoading(false)
    }
  }, [obDebouncedSearch, obPage, obFilter, obSortBy, obSortDir])

  useEffect(() => {
    if (activeTab === 'users') loadUsers()
  }, [loadUsers, activeTab])

  useEffect(() => {
    if (activeTab === 'onboarding') loadOnboardingData()
  }, [loadOnboardingData, activeTab])

  const handlePromote = async (userId: string) => {
    try {
      setActionLoading(userId)
      await apiClient.promoteToCaptain(userId)
      showSuccessToast('User promoted to captain')
      await loadUsers()
    } catch (err) {
      handleApiError(err as any, 'promote user')
    } finally {
      setActionLoading(null)
    }
  }

  const handleDemote = async (userId: string) => {
    try {
      setActionLoading(userId)
      await apiClient.demoteCaptain(userId)
      showSuccessToast('Captain demoted to pirate')
      await loadUsers()
    } catch (err) {
      handleApiError(err as any, 'demote captain')
    } finally {
      setActionLoading(null)
    }
  }

  const handleObSort = (col: string) => {
    if (obSortBy === col) {
      setObSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setObSortBy(col)
      setObSortDir('asc')
    }
    setObPage(1)
  }

  const SortIcon = ({ col }: { col: string }) => {
    if (obSortBy !== col) return <ArrowUpDown className="w-3 h-3 text-gray-600" />
    return obSortDir === 'asc'
      ? <ArrowUp className="w-3 h-3 text-green-400" />
      : <ArrowDown className="w-3 h-3 text-green-400" />
  }

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white flex items-center gap-3">
          <ShieldCheck className="w-7 h-7 text-green-400" />
          User Management
        </h1>
        <p className="text-gray-400 mt-1 text-sm">
          Manage user roles and view onboarding data.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-0 mb-6 border-b border-gray-700">
        <button
          onClick={() => setActiveTab('users')}
          className={`px-5 py-2.5 text-sm font-medium transition-colors ${
            activeTab === 'users'
              ? 'text-green-400 border-b-2 border-green-400'
              : 'text-gray-400 hover:text-gray-300'
          }`}
        >
          Users
        </button>
        <button
          onClick={() => setActiveTab('onboarding')}
          className={`px-5 py-2.5 text-sm font-medium transition-colors ${
            activeTab === 'onboarding'
              ? 'text-green-400 border-b-2 border-green-400'
              : 'text-gray-400 hover:text-gray-300'
          }`}
        >
          Onboarding Data
        </button>
      </div>

      {activeTab === 'users' && (
        <>
          {/* Info callout */}
          <div className="bg-blue-900/30 border border-blue-800 rounded-xl p-4 mb-6 flex gap-3">
            <Info className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
            <div className="text-sm text-blue-200">
              <strong>Captain access:</strong> Promoted captains can view all courses across every organization, run live lesson sessions, and access the lesson builder & analytics. They do not need individual course access grants.
            </div>
          </div>

          {/* Search */}
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-green-500 text-sm"
            />
          </div>

          {/* Table */}
          <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
            {loading ? (
              <div className="text-center py-16 text-gray-400">
                <div className="animate-spin w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full mx-auto mb-3" />
                Loading users...
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-16 text-gray-500">
                No users found.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-700 text-gray-400 text-left text-xs uppercase tracking-wider">
                    <th className="px-5 py-3">User</th>
                    <th className="px-5 py-3">Role</th>
                    <th className="px-5 py-3 hidden sm:table-cell">Joined</th>
                    <th className="px-5 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b border-gray-700/50 hover:bg-gray-700/30 transition-colors">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <img
                            src={getAvatarUrl(u.display_name, u.avatar_url)}
                            alt=""
                            className="w-8 h-8 rounded-full shrink-0"
                          />
                          <div className="min-w-0">
                            <div className="text-white font-medium truncate">
                              {u.display_name || 'No name'}
                            </div>
                            <div className="text-gray-400 text-xs truncate">{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <span
                          className={`inline-block text-xs px-2 py-0.5 rounded font-medium ${
                            u.role === 'captain'
                              ? 'bg-blue-900/50 text-blue-300'
                              : 'bg-gray-700 text-gray-300'
                          }`}
                        >
                          {u.role || 'pirate'}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-gray-400 text-xs hidden sm:table-cell">
                        {u.created_at ? formatRelativeDate(u.created_at) : '-'}
                      </td>
                      <td className="px-5 py-3 text-right">
                        {u.role === 'pirate' ? (
                          <button
                            onClick={() => handlePromote(u.id)}
                            disabled={actionLoading === u.id}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-900/50 text-blue-300 rounded-lg hover:bg-blue-900 transition-colors text-xs disabled:opacity-50"
                          >
                            <ArrowUpCircle className="w-3.5 h-3.5" />
                            Promote
                          </button>
                        ) : u.role === 'captain' ? (
                          <button
                            onClick={() => handleDemote(u.id)}
                            disabled={actionLoading === u.id}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-900/50 text-orange-300 rounded-lg hover:bg-orange-900 transition-colors text-xs disabled:opacity-50"
                          >
                            <ArrowDownCircle className="w-3.5 h-3.5" />
                            Demote
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {pagination && pagination.total_pages > 1 && (
            <div className="flex items-center justify-between mt-4 text-sm text-gray-400">
              <span>
                Page {pagination.current_page} of {pagination.total_pages} ({pagination.total_count} users)
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => p - 1)}
                  disabled={!pagination.has_prev_page}
                  className="flex items-center gap-1 px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Prev
                </button>
                <button
                  onClick={() => setPage((p) => p + 1)}
                  disabled={!pagination.has_next_page}
                  className="flex items-center gap-1 px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {activeTab === 'onboarding' && (
        <>
          {/* Search + Filter */}
          <div className="flex gap-3 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={obSearch}
                onChange={(e) => setObSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-green-500 text-sm"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <select
                value={obFilter}
                onChange={(e) => { setObFilter(e.target.value as any); setObPage(1) }}
                className="pl-9 pr-4 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-green-500 appearance-none cursor-pointer"
              >
                <option value="all">All</option>
                <option value="completed">Completed</option>
                <option value="incomplete">Incomplete</option>
              </select>
            </div>
          </div>

          {/* Onboarding Table */}
          <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-x-auto">
            {obLoading ? (
              <div className="text-center py-16 text-gray-400">
                <div className="animate-spin w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full mx-auto mb-3" />
                Loading onboarding data...
              </div>
            ) : obUsers.length === 0 ? (
              <div className="text-center py-16 text-gray-500">
                No users found.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-700 text-gray-400 text-left text-xs uppercase tracking-wider">
                    <th className="px-4 py-3">
                      <button onClick={() => handleObSort('name')} className="flex items-center gap-1 hover:text-white transition-colors">
                        User <SortIcon col="name" />
                      </button>
                    </th>
                    <th className="px-4 py-3">
                      <button onClick={() => handleObSort('country')} className="flex items-center gap-1 hover:text-white transition-colors">
                        Country <SortIcon col="country" />
                      </button>
                    </th>
                    <th className="px-4 py-3">
                      <button onClick={() => handleObSort('institution')} className="flex items-center gap-1 hover:text-white transition-colors">
                        Institution <SortIcon col="institution" />
                      </button>
                    </th>
                    <th className="px-4 py-3">Grades</th>
                    <th className="px-4 py-3">Subjects</th>
                    <th className="px-4 py-3">
                      <button onClick={() => handleObSort('using_pp')} className="flex items-center gap-1 hover:text-white transition-colors">
                        Using PP? <SortIcon col="using_pp" />
                      </button>
                    </th>
                    <th className="px-4 py-3 text-center">
                      <button onClick={() => handleObSort('status')} className="flex items-center gap-1 hover:text-white transition-colors mx-auto">
                        Status <SortIcon col="status" />
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {obUsers.map((u) => (
                    <tr key={u.id} className="border-b border-gray-700/50 hover:bg-gray-700/30 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <img
                            src={getAvatarUrl(u.display_name, u.avatar_url)}
                            alt=""
                            className="w-8 h-8 rounded-full shrink-0"
                          />
                          <div className="min-w-0">
                            <div className="text-white font-medium truncate">
                              {u.display_name || 'No name'}
                            </div>
                            <div className="text-gray-400 text-xs truncate">{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-300 text-xs">
                        {u.country || <span className="text-gray-600">-</span>}
                      </td>
                      <td className="px-4 py-3 text-gray-300 text-xs max-w-[160px] truncate">
                        {u.institution || <span className="text-gray-600">-</span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {u.grades.length > 0 ? u.grades.map((g) => (
                            <span key={g} className="inline-block text-xs px-1.5 py-0.5 bg-purple-900/40 text-purple-300 rounded">
                              {g}
                            </span>
                          )) : <span className="text-gray-600 text-xs">-</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {u.subjects.length > 0 ? u.subjects.map((s) => (
                            <span key={s} className="inline-block text-xs px-1.5 py-0.5 bg-blue-900/40 text-blue-300 rounded">
                              {s}
                            </span>
                          )) : <span className="text-gray-600 text-xs">-</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-300 text-xs">
                        {u.using_potato_pirates || <span className="text-gray-600">-</span>}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {u.onboarding_completed ? (
                          <Check className="w-4 h-4 text-green-400 mx-auto" />
                        ) : (
                          <X className="w-4 h-4 text-gray-600 mx-auto" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Onboarding Pagination */}
          {obPagination && obPagination.total_pages > 1 && (
            <div className="flex items-center justify-between mt-4 text-sm text-gray-400">
              <span>
                Page {obPagination.current_page} of {obPagination.total_pages} ({obPagination.total_count} users)
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setObPage((p) => p - 1)}
                  disabled={!obPagination.has_prev_page}
                  className="flex items-center gap-1 px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Prev
                </button>
                <button
                  onClick={() => setObPage((p) => p + 1)}
                  disabled={!obPagination.has_next_page}
                  className="flex items-center gap-1 px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
